import { useEffect, useMemo, useState } from "react";
import { Plus, Share, X } from "lucide-react";

const DISMISS_KEY = "oikos:add-to-home-dismissed";
const FALLBACK_COLOR = "#ff7a00";

function getThemeColor() {
  return document.querySelector('meta[name="theme-color"]')?.getAttribute("content") || FALLBACK_COLOR;
}

function hexToRgb(color = "") {
  const clean = String(color || "").trim().replace("#", "");
  const normalized = clean.length === 3
    ? clean.split("").map((item) => `${item}${item}`).join("")
    : clean;
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function colorWithAlpha(color = "", alpha = 1) {
  const rgb = hexToRgb(color);
  if (!rgb) return `rgba(255, 122, 0, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function isStandaloneDisplay() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isTouchDevice() {
  return (
    window.matchMedia?.("(pointer: coarse)")?.matches ||
    navigator.maxTouchPoints > 1
  );
}

function isAppleMobile() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

export default function AddToHomeScreenPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [themeColor, setThemeColor] = useState(() => (
    typeof document === "undefined" ? FALLBACK_COLOR : getThemeColor()
  ));
  const isApple = useMemo(() => {
    if (typeof window === "undefined") return false;
    return isAppleMobile();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandaloneDisplay() || !isTouchDevice()) return undefined;
    if (window.localStorage.getItem(DISMISS_KEY) === "1") return undefined;

    const timer = window.setTimeout(() => setVisible(true), 1400);

    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      setInstallPrompt(event);
      setVisible(true);
    }

    function handleInstalled() {
      setVisible(false);
      setInstallPrompt(null);
      window.localStorage.setItem(DISMISS_KEY, "1");
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const observer = new MutationObserver(() => setThemeColor(getThemeColor()));
    if (themeMeta) {
      observer.observe(themeMeta, { attributes: true, attributeFilter: ["content"] });
    }
    setThemeColor(getThemeColor());
    return () => observer.disconnect();
  }, []);

  async function installApp() {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice.catch(() => null);
    setInstallPrompt(null);
    setVisible(false);
    window.localStorage.setItem(DISMISS_KEY, "1");
  }

  function dismiss() {
    setVisible(false);
    window.localStorage.setItem(DISMISS_KEY, "1");
  }

  if (!visible) return null;

  const canPrompt = Boolean(installPrompt);

  return (
    <aside
      style={{
        ...styles.wrap,
        borderColor: colorWithAlpha(themeColor, 0.34),
        background: `linear-gradient(135deg, ${colorWithAlpha(themeColor, 0.96)}, ${colorWithAlpha(themeColor, 0.84)})`,
      }}
      aria-label="Add Oikos EDU to home screen"
    >
      <div style={{ ...styles.iconShell, background: themeColor }}>
        <img src="/oikos-edu-icon.svg" alt="" style={styles.icon} />
      </div>
      <div style={styles.copy}>
        <strong style={styles.title}>Add Oikos EDU to Home Screen</strong>
        <span style={styles.detail}>
          {canPrompt
            ? "Install the app for quick access."
            : isApple
              ? "Tap Share, then Add to Home Screen."
              : "Use your browser menu to install this app."}
        </span>
      </div>
      {canPrompt ? (
        <button type="button" style={{ ...styles.primaryButton, color: themeColor }} onClick={installApp}>
          <Plus size={16} />
          Add
        </button>
      ) : (
        <span style={{ ...styles.shareHint, background: colorWithAlpha(themeColor, 0.18) }} aria-hidden="true">
          <Share size={16} />
        </span>
      )}
      <button
        type="button"
        style={{ ...styles.closeButton, background: colorWithAlpha(themeColor, 0.16) }}
        onClick={dismiss}
        aria-label="Dismiss add to home screen prompt"
      >
        <X size={16} />
      </button>
    </aside>
  );
}

const styles = {
  wrap: {
    position: "fixed",
    zIndex: 9999,
    left: "max(12px, env(safe-area-inset-left))",
    right: "max(12px, env(safe-area-inset-right))",
    bottom: "max(12px, env(safe-area-inset-bottom))",
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto auto",
    alignItems: "center",
    gap: 10,
    maxWidth: 560,
    margin: "0 auto",
    padding: 10,
    borderRadius: 8,
    border: "1px solid rgba(254, 215, 170, 0.45)",
    background: "rgba(67, 20, 7, 0.94)",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.28)",
    color: "#fff7ed",
    backdropFilter: "blur(16px)",
  },
  iconShell: {
    width: 42,
    height: 42,
    borderRadius: 8,
    overflow: "hidden",
    background: FALLBACK_COLOR,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
  },
  icon: {
    width: "100%",
    height: "100%",
    display: "block",
  },
  copy: {
    display: "grid",
    gap: 2,
    minWidth: 0,
  },
  title: {
    fontSize: 13,
    lineHeight: 1.2,
  },
  detail: {
    color: "#fed7aa",
    fontSize: 12,
    lineHeight: 1.25,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    minHeight: 34,
    border: 0,
    borderRadius: 8,
    background: "#fff7ed",
    color: FALLBACK_COLOR,
    fontSize: 13,
    fontWeight: 800,
    padding: "0 12px",
  },
  shareHint: {
    display: "inline-grid",
    placeItems: "center",
    width: 34,
    height: 34,
    borderRadius: 8,
    background: "rgba(255,255,255,0.12)",
    color: "#fed7aa",
  },
  closeButton: {
    display: "inline-grid",
    placeItems: "center",
    width: 34,
    height: 34,
    border: 0,
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
    color: "#fff7ed",
  },
};
